import { get } from "lodash";
import { ENDPOINTS } from "../constants/api.js";

import {
  WORKSTATIONS,
  DISPLAY_GATEWAYS,
  CONTAINERS,
  NODES,
  DATABASES,
  SNOWFLAKES,
  VPN_GATEWAYS,
  IMAGES,
  ENTITIES,
  GROUPS
} from "../state/actions/actionConstants.js";

import {
  AT_TYPES,
  RESOURCE_KEYS,
  RESOURCE_URI_CHUNKS
} from "../constants/entity.js";

export function getActionEntityByType(type) {
  switch (type) {
    case RESOURCE_KEYS.WORKSTATIONS:
    case RESOURCE_URI_CHUNKS.WORKSTATIONS:
    case AT_TYPES.WORKSTATION:
      return WORKSTATIONS;
    case RESOURCE_KEYS.DISPLAY_GATEWAYS:
    case RESOURCE_URI_CHUNKS.DISPLAY_GATEWAYS:
    case AT_TYPES.DISPLAY_GATEWAY:
      return DISPLAY_GATEWAYS;
    case RESOURCE_KEYS.CONTAINERS:
    case RESOURCE_URI_CHUNKS.CONTAINERS:
    case AT_TYPES.CONTAINER:
      return CONTAINERS;
    case RESOURCE_KEYS.DATABASES:
    case RESOURCE_URI_CHUNKS.DATABASES:
    case AT_TYPES.DATABASE:
      return DATABASES;
    case RESOURCE_KEYS.NODES:
    case RESOURCE_URI_CHUNKS.NODES:
    case AT_TYPES.NODE:
      return NODES;
    case RESOURCE_KEYS.SNOWFLAKES:
    case RESOURCE_URI_CHUNKS.SNOWFLAKES:
    case AT_TYPES.SNOWFLAKE:
      return SNOWFLAKES;
    case RESOURCE_KEYS.VPN_GATEWAYS:
    case RESOURCE_URI_CHUNKS.VPN_GATEWAYS:
    case AT_TYPES.VPN_GATEWAY:
      return VPN_GATEWAYS;
    case RESOURCE_KEYS.IMAGES:
    case RESOURCE_URI_CHUNKS.IMAGES:
    case AT_TYPES.IMAGES:
      return IMAGES;
    case AT_TYPES.USER_GROUP:
      return ENTITIES;
    case AT_TYPES.GROUP:
      return GROUPS;
    default:
      return null;
  }
}

export function buildAtId(data, typeOveride = undefined) {
  const { type, id } = data;

  const finalType = typeOveride || type;

  return `/${finalType}/${id}`;
}

// might be worth making this part of the data structure
// however really want to keep the main structure as flat as possible
// flattening the resource api calls would help a lot with that
export function buildResourceArray(data) {
  return [
    {
      data: data[RESOURCE_KEYS.WORKSTATIONS],
      type: RESOURCE_KEYS.WORKSTATIONS,
      actionEntity: WORKSTATIONS,
      endpoint: ENDPOINTS.WORKSTATIONS
    },
    {
      data: data[RESOURCE_KEYS.DISPLAY_GATEWAYS],
      type: RESOURCE_KEYS.DISPLAY_GATEWAYS,
      actionEntity: DISPLAY_GATEWAYS,
      endpoint: ENDPOINTS.DISPLAY_GATEWAYS
    },
    {
      data: data[RESOURCE_KEYS.CONTAINERS],
      type: RESOURCE_KEYS.CONTAINERS,
      actionEntity: CONTAINERS,
      endpoint: ENDPOINTS.CONTAINERS
    },
    {
      data: data[RESOURCE_KEYS.DATABASES],
      type: RESOURCE_KEYS.DATABASES,
      actionEntity: DATABASES,
      endpoint: ENDPOINTS.DATABASES
    },
    {
      data: data[RESOURCE_KEYS.NODES],
      type: RESOURCE_KEYS.NODES,
      actionEntity: NODES,
      endpoint: ENDPOINTS.NODES
    },
    {
      data: data[RESOURCE_KEYS.SNOWFLAKES],
      type: RESOURCE_KEYS.SNOWFLAKES,
      actionEntity: SNOWFLAKES,
      endpoint: ENDPOINTS.SNOWFLAKES
    },
    {
      data: data[RESOURCE_KEYS.VPN_GATEWAYS],
      type: RESOURCE_KEYS.VPN_GATEWAYS,
      actionEntity: VPN_GATEWAYS,
      endpoint: ENDPOINTS.VPN_GATEWAYS
    }
  ];
}

// extract resource details from a project object using the resource @id
export function getResourceFromProjectByAtId(project, id) {
  const resources = project.workstations.concat(
    project.displayGateways,
    project.containers,
    project.databases,
    project.nodes,
    project.snowflakes,
    project.virtualPrivateNetworkGateways
  );

  return resources.find(resource => resource["@id"] === id);
}

// pagination helpers
export function fetchEntitiesByPage(page, location, force) {
  const urlPage = getURLPageParam(location);

  if ((urlPage && urlPage !== page) || (urlPage && force)) {
    return urlPage;
  } else if (page && force) {
    return page;
  } else if (!page) {
    return 1;
  }
}

export function getURLFilterParams(location) {
  const params = new URLSearchParams(location.search);

  // filters have an annoying structure
  const result = {};
  for (let p of params) {
    const orderIndex = p[0].indexOf("order[");

    if (p[0] === "page") {
      // pages have to parsed as integers
      result.page = parseInt(p[1], 10);
    } else if (orderIndex > -1) {
      // deconstruct filter array structure
      const order = p[0].substring(orderIndex + 6, p[0].lastIndexOf("]"));
      result.order = order;
      result.direction = p[1];
    } else {
      // store anything else as is
      result[p[0]] = p[1];
    }
  }

  return result;
}

export function updateFilterSearchParams(searchParamStr, filters) {
  const params = new URLSearchParams(searchParamStr);

  // delete any current order value
  // as the param name changes this won't necesserily be overwritten
  for (let p of params) {
    const orderIndex = p[0].indexOf("order[");
    if (orderIndex > -1) {
      params.delete(p[0]);
    }
  }

  // reset page otherwise we can end up on an empty page after filtering
  params.set("page", 1);

  // update params based on passed filter values
  Object.keys(filters).forEach(key => {
    if (key === "order") {
      params.set(`order[${filters[key]}]`, filters.direction || "asc");
    } else if (key === "direction") {
      // ignore direction for now
    } else {
      params.set(key, filters[key]);
    }
  });

  return params;
}

export function updatePageSearchParams(searchParamStr, page) {
  const params = new URLSearchParams(searchParamStr);

  if (page) {
    params.set("page", page);
  }

  return params;
}

export function createFilteredURL(location, filters) {
  const searchParams = createURLFilterParams(location, filters);
  return `${location.pathname}?${searchParams.toString()}`;
}

export function getURLPageParam(location) {
  const params = new URLSearchParams(location.search);
  const page = parseInt(params.get("page"), 10);
  return isNaN(page) ? null : page;
}

// removes any password details from the passed action - but not very well ...
export function sanitiseAction(action) {
  const sanitisedAction = { ...action };

  delete sanitisedAction.payload.password;
  delete sanitisedAction.payload.passwordConfirmation;
  delete sanitisedAction.payload.oldPassword;

  return sanitisedAction;
}

// masage hydra result so that the data is always wrapped in an array
// simplifies parse logic elsewhere
export function getHydraResultAsArray(hydraResult) {
  if (Object.hasOwn(hydraResult, "hydra:member")) {
    return get(hydraResult, "hydra:member", []);
  }

  if (Object.hasOwn(hydraResult, "@id")) {
    return [hydraResult];
  }

  console.warn(
    "utils.getHydraResultAsArray :: supplied hydraResult could not be wrapped ::",
    hydraResult
  );
  return hydraResult;
}
